<template>
  <div id="kanbanContainer">
    <div id="toolbar"></div>
    <div id="root"></div>
    <EditTask ref="editTask" @onEdit="onEditTask($event)" />
    <FacilitySelectDialog ref="facilityDialog" />

    <!--
      Centralized copy-link menu — single instance reused across all task
      cards. Positioned at click coordinates via :position-x / :position-y,
      populated on demand by openCopyLinkMenu(taskId, event). Items carry
      intent-aware icons (cube / focus / view-dashboard) so users see at a
      glance what each link will open on the recipient's side.
    -->
    <v-menu
      v-model="copyLinkMenu.show"
      :position-x="copyLinkMenu.x"
      :position-y="copyLinkMenu.y"
      absolute
      offset-y
      :close-on-content-click="true">
      <v-card class="copy-link-menu" elevation="8">
        <v-list dense class="copy-link-menu__list">
          <v-subheader class="copy-link-menu__header">
            <v-icon small class="mr-2">mdi-link-variant</v-icon>
            Copy link for facility
          </v-subheader>
          <v-divider></v-divider>
          <v-list-item
            v-for="item in copyLinkMenu.items"
            :key="item.facilityId"
            class="copy-link-menu__item"
            @click="pickCopyLinkFacility(item)">
            <v-list-item-icon class="mr-3">
              <v-icon small :color="item.intent === 'task-board' ? 'grey' : 'warning'">
                {{ item.icon }}
              </v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="copy-link-menu__name">
                {{ item.facilityName }}
              </v-list-item-title>
              <v-list-item-subtitle class="copy-link-menu__intent">
                {{ item.intent === 'task-board' ? 'Open in task board' : 'Open in 3D / 360 viewer' }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-card>
    </v-menu>
  </div>
</template>

<script>
  /* eslint-disable */
  const { Kanban, RestDataProvider, defaultCardShape, template, getDefaultCardMenuItems } = kanban;
  import { retryAddStatus3dOption } from '../utils/kanban';
  import { copyTaskLinkToClipboard } from '@/utils/shareIntents';
  import {
    getTaskIsolationInfo,
    getIsolateClickBehavior,
    getIsolateButtonColor,
    getIsolateTooltip,
    getIsolateIcon,
    getTaskEffectiveTagIds,
    navigateToFacility,
    prefetchTasksIsolation,
    getCopyLinkBehavior,
    getCopyLinkButtonColor,
    getCopyLinkMenuItems,
    getCopyLinkDirectTarget,
  } from '@Task/utils/taskIsolation';

  import config from '@/../appConfig';
  import { mapGetters } from 'vuex';
  import EditTask from '@Task/components/EditTask.vue';
  import FacilitySelectDialog from '@Task/components/FacilitySelectDialog.vue';
  import dayjs from 'dayjs';
  import { error, log as debugLog } from '@/utils/debugLogger';
  import { $has } from '@/utils';
  import permissions from '@Share/config/permissions';
  import { DELETE_SHORTCUT, deleteShortcutDecision } from '@Task/utils/kanbanKeyboard';
  import { buildKanbanCards } from '@Task/utils/kanbanCards';

  const PERMISSION_MESSAGES = {
    delete: 'You do not have permission to delete tasks.',
    create: 'You do not have permission to create tasks.',
  };
  export default {
    name: 'app-kanban',
    data() {
      return {
        board: {},
        cardShape: {},
        status3dUpdateTimeout: null,
        eventsInitialized: false,
        highlightedTaskId: null,
        // Keyboard scope. keep-alive keeps the board alive behind the List/Gantt/Schedule tabs,
        // so the board acts on a key only while it is the shown tab (`isActive`) and the user's
        // last pointer or focus event was inside it (`boardFocused`).
        isActive: false,
        boardFocused: false,
        // Per-task isolation cache. Populated by prefetchTaskIsolations after
        // each entries change; keys are task ids, values are the result of
        // taskIsolation.getTaskIsolationInfo. cardTemplate reads this map
        // synchronously to decide visibility/color/tooltip for each card's
        // isolate button.
        taskIsolationMap: {},
        // In-flight flags so cardTemplate can render a loading skeleton.
        taskIsolationLoading: {},
        // Copy-link menu state — single inline v-menu (declared in template,
        // see <v-menu> after FacilitySelectDialog) reused across all task
        // cards. Positioned at the click coordinates so it appears next to
        // whichever card was clicked. Mirrors the inline-menu pattern used
        // by TaskCopyLinkButton.vue (Vue-component path) but lives at the
        // view level here because cards are rendered via the SVAR Kanban
        // library's HTML-string templates, not Vue components.
        copyLinkMenu: {
          show: false,
          x: 0,
          y: 0,
          taskId: null,
          items: [],
        },
      };
    },
    mounted() {
      this.configKanban();
      this.adjustKanbantHeight();
      window.addEventListener('resize', this.adjustKanbantHeight);
      this.debounceStatus3DUpdate();
      this.updateHighlightedTask();
    },
    computed: {
      ...mapGetters('TASK', {
        loading: 'GET_LOADING',
        entries: 'GET_TASKS',
        total: 'GET_TOTAL',
      }),
      ...mapGetters('USER', {
        users: 'GET_ENTRIES',
      }),
      ...mapGetters('TASK_MANAGER', {
        taskTypes: 'GET_ENTRIES',
      }),
      ...mapGetters('PROJECT', {
        projects: 'GET_ENTRIES',
      }),
      lastIsolatedStatusId: {
        get() {
          return this.$store.getters['TASK/GET_LAST_ISOLATED_STATUS_ID'];
        },
        set(value) {
          this.$store.commit('TASK/SET_LAST_ISOLATED_STATUS_ID', value);
        },
      },
      options: {
        get() {
          return this.$store.getters['TASK/GET_OPTIONS'];
        },
        set(val) {
          this.$store.commit('TASK/SET_OPTIONS', val);
        },
      },
      currentTaskType() {
        return this.$store.getters['TASK_MANAGER/GET_CURRENT_TASK_TYPE'];
      },
      taskType() {
        let type = this.taskTypes.find((e) => e.id == this.$route.params.taskTypeId) || {
          statuses: [],
        };
        if (type.statuses.length > 0) {
          type.statuses.sort((a, b) => a.order - b.order);
        }
        return type;
      },
      kanbanUsers() {
        return this.users.map((u) => ({ id: u.id, label: u.displayName, avatar: u.avatarUrl }));
      },
      isolatedTasks: {
        get() {
          return this.$store.getters['TASK/GET_ISOLATED_TASKS'];
        },
        set(value) {
          this.$store.commit('TASK/SET_ISOLATED_TASKS', value);
        },
      },
      /**
       * Facility dictionary (id → name) from store for cross-asset isolation.
       */
      facilityDict() {
        return this.$store.getters.getFacilityDict || {};
      },
    },
    methods: {
      locateID(el, attr = 'data-id') {
        const node = this.locate(el, attr);
        if (node) {
          return node.getAttribute(attr);
        }
        return null;
      },
      locate(el, attr = 'data-id') {
        let node = el;
        if (!node.tagName && el.target) node = el.target;
        while (node) {
          if (node.getAttribute) {
            const id2 = node.getAttribute(attr);
            if (id2) return node;
          }
          node = node.parentNode;
        }
        return null;
      },
      canDeleteTasks() {
        return $has(permissions.Tasks.Delete);
      },
      canCreateTasks() {
        return $has(permissions.Tasks.Create);
      },
      /** Listen for the board's keys while it is the shown tab (see `isActive`). */
      attachBoardKeyboard() {
        this.isActive = true;
        // Capture phase: the board runs before the DHTMLX library's own document Delete hotkey,
        // and stops it when the board handles the key, so one press means one confirm.
        document.addEventListener('keydown', this.onKeyPressed, true);
        document.addEventListener('pointerdown', this.trackBoardFocus, true);
        document.addEventListener('focusin', this.trackBoardFocus, true);
      },
      detachBoardKeyboard() {
        this.isActive = false;
        this.boardFocused = false;
        document.removeEventListener('keydown', this.onKeyPressed, true);
        document.removeEventListener('pointerdown', this.trackBoardFocus, true);
        document.removeEventListener('focusin', this.trackBoardFocus, true);
      },
      /** Cards are not focusable, so "focus on the board" is the last pointer or focus event. */
      trackBoardFocus(event) {
        this.boardFocused = !!this.$el && this.$el.contains(event.target);
      },
      onKeyPressed(e) {
        const decision = deleteShortcutDecision(e, {
          active: this.isActive,
          boardFocused: this.boardFocused,
          canDelete: this.canDeleteTasks(),
        });
        if (decision === DELETE_SHORTCUT.IGNORE) return;

        e.preventDefault();
        e.stopPropagation();
        if (decision === DELETE_SHORTCUT.DENIED) {
          this.$dialog.notify.warning(PERMISSION_MESSAGES.delete);
          return;
        }
        const selected = this.board.getSelection();
        if (selected && selected.length) this.confirmDeleteTasks(selected);
      },
      /** The one delete flow for the board: permission, confirm, then TASK/DELETE. */
      confirmDeleteTasks(taskIds) {
        if (!this.canDeleteTasks()) {
          this.$dialog.notify.warning(PERMISSION_MESSAGES.delete);
          return;
        }
        this.$dialog.error({
          title: 'Delete Task',
          text: 'Are you sure you want to delete this task?',
          persistent: true,
          actions: {
            false: 'Cancel',
            true: {
              text: 'Ok',
              color: 'primary',
              handle: () => {
                this.$store.dispatch('TASK/DELETE', taskIds);
              },
            },
          },
        });
      },
      duplicateTask(taskId) {
        if (!this.canCreateTasks()) {
          this.$dialog.notify.warning(PERMISSION_MESSAGES.create);
          return;
        }
        return this.$store.dispatch('TASK/DUPLICATE', taskId).then((task) => {
          this.$refs.editTask.open({
            task: task,
            isNew: false,
            taskTypeId: this.$route.params.taskTypeId,
          });
        }, () => {
          // Refused: TASK/DUPLICATE already showed the one message, and no dialog opens.
          // (then's second argument: an error after the duplicate is no refusal.)
        });
      },
      cardMenuItems(card) {
        return [
          {
            id: 'duplicate',
            icon: 'wxi-duplicate',
            text: 'Duplicate',
            disabled: !this.canCreateTasks(),
            handler: () => {
              this.duplicateTask(card.id);
              return true;
            },
          },
          {
            id: 'delete',
            icon: 'wxi-delete-outline',
            text: 'Delete',
            disabled: !this.canDeleteTasks(),
            handler: () => {
              this.confirmDeleteTasks([card.id]);
            },
          },
        ];
      },
      /**
       * Hand the board the store's tasks. Every load builds the cards fresh: the board mutates the
       * card objects it holds (a move sets the card's column in place), so cards cached from an
       * earlier load would put a refused card back in the column the server refused.
       */
      reloadBoardFromStore() {
        this.board.parse(this.boardData(buildKanbanCards(this.entries, this.kanbanUsers)));
      },
      /** The board's columns and row around `cards`. */
      boardData(cards) {
        return {
          cards,
          columns: this.currentTaskType?.statuses?.map((e) => {
            return { id: e.id, label: e.name, color: e.color };
          }),
          rows: [
            {
              id: this.currentTaskType?.id ?? 'default',
              label: this.currentTaskType?.name ?? 'Default',
            },
          ],
        };
      },
      /**
       * The board has already moved the card when `move-card` fires. A refused move leaves the
       * store's task in its old column, so the board is reloaded from the store's tasks.
       * TASK/MOVE shows the one message.
       */
      moveCardHandler(ev) {
        let areaCards = this.board.api.getAreaCards(ev.columnId);
        let orderIndex = areaCards.findIndex((e) => e.id == ev.id);
        ev.order = orderIndex > -1 ? orderIndex : areaCards.length - 1;
        return this.$store.dispatch('TASK/MOVE', ev).then(this.onCardMoved, this.onCardMoveRefused);
      },
      onCardMoved() {
        this.$emit('updateViewerStatusColors');

        if (this.lastIsolatedStatusId) {
          showStatusIn3D(this.lastIsolatedStatusId);
        }

        this.debounceStatus3DUpdate();
      },
      onCardMoveRefused() {
        this.reloadBoardFromStore();
        this.debounceStatus3DUpdate();
      },
      deleteCardHandler(ev) {
        this.$store.dispatch('TASK/DELETE', [ev.id]).then(() => {});
      },
      /**
       * `delete-card` reaches the library from two places, and neither may delete here:
       * - the library's own Delete hotkey, which has no tab or permission check. The board's
       *   keyboard handler (onKeyPressed) owns Delete instead.
       * - TASK/DELETE's deleteFromComponents (task.modules.js), after the server delete. The card
       *   already leaves the board when the entries watcher re-parses it.
       * The card menu deletes through confirmDeleteTasks, never through `delete-card`.
       */
      deleteCardIntercept() {
        return false;
      },
      /**
       * The library's Ctrl+D hotkey duplicates a card in the board only, never on the server, and
       * with no permission check. Refused; the card menu's Duplicate is the real one.
       */
      duplicateCardIntercept() {
        return false;
      },
      addCardIntercept(ev) {
        if (ev.app_handled) return false;
        if (!this.canCreateTasks()) {
          this.$dialog.notify.warning(PERMISSION_MESSAGES.create);
          return false;
        }
        let today = new Date().toISOString();
        let task = {
          text: '',
          start_date: today,
          end_date: today,
          actualStartDate: null,
          actualEndDate: null,
          statusId: ev.columnId,
        };
        let payLoad = {
          task,
          isNew: true,
          fromIndexPage: false,
          taskTypeId: this.$route.params.taskTypeId,
          isBulk: false,
        };
        this.$refs.editTask.open(payLoad);
        return false;
      },
      onSetEditTask(ev) {
        if (ev?.cardId) {
          let task = this.board.getCard(ev.cardId);
          let payload = {
            task,
            isNew: task.id.startsWith('temp'),
            taskTypeId: this.taskType.id,
          };
          this.$refs.editTask.open(payload);
        }
        return false;
      },
      onMoveRow(ev) {
        send(`rows/${ev.id}/move`, 'PUT', ev);
      },
      onMoveColumn(ev) {
        send(`columns/${ev.id}/move`, 'PUT', ev);
      },
      configEvents(force = false) {
        if (!this.board?.api || (this.eventsInitialized && !force)) return;

        this.eventsInitialized = true;

        board.api.on('move-column', this.onMoveColumn);
        board.api.on('move-row', this.onMoveRow);
        board.api.on('add-card', (ev) => {
          return false;
        });
        board.api.on('update-card', (obj) => {});
        board.api.on('move-card', this.moveCardHandler);
        board.api.on('delete-card', this.deleteCardHandler);

        board.api.intercept('delete-card', this.deleteCardIntercept);
        board.api.intercept('duplicate-card', this.duplicateCardIntercept);
        board.api.intercept('add-card', this.addCardIntercept);
        board.api.intercept('set-edit', (ev) => {
          return false;
        });

        const container = document.getElementsByClassName('wx-kanban')[0];
        // on double click on the card, open the edit task dialog
        container.addEventListener('dblclick', (ev) => {
          const id = this.locateID(ev.target);
          if (!id) return;
          this.onSetEditTask({ cardId: id });
        });
      },

      configKanban() {
        this.cardShape = {
          ...defaultCardShape,
          label: true,
          description: true,
          progress: true,
          start_date: true,
          end_date: true,
          priority: true,
          color: true,
          cover: true,
          attached: true,
          comments: true,
          users: {
            show: true,
            values: this.kanbanUsers,
          },
          menu: {
            show: true,
            items: ({ card }) => this.cardMenuItems(card),
          },
        };

        const send = (partUrl, method, data) => {
          const headers = {
            'Content-Type': 'application/json',
          };
          const req = {
            method,
            headers,
          };
          if (data) {
            req.body = typeof data === 'object' ? JSON.stringify(data) : data;
          }
          return fetch(`${url}/${partUrl}`, req).then((res) => res.json());
        };

        const board = new Kanban('#root', {
          cards: buildKanbanCards(this.entries, this.kanbanUsers),
          columns: this.taskType.statuses.map((e) => {
            return { id: e.id, label: e.name, color: e.color };
          }),
          rows: [
            {
              id: this.taskType.id,
              label: this.taskType.name,
            },
          ],
          rowKey: 'typeId',
          cardShape: this.cardShape,
          cardTemplate: template((card) => this.cardTemplate(card)),
          columnKey: 'statusId',
          theme: {
            name: this.$vuetify.theme.isDark ? 'willow-dark' : 'willow',
            fonts: true,
          },
          scrollType: 'column',
          renderType: 'lazy',
        });

        this.eventsInitialized = false;

        this.board = board;
        window.board = board;

        const state = board.api.getReactiveState();

        // subscribe on the cards changes and output the array of cards
        state.cards.subscribe((data) => {
          this.debounceStatus3DUpdate();
        });

        setTimeout(() => {
          this.configEvents();
        }, 0);

        window.showIn3D = (id) => {
          this.lastIsolatedStatusId = null;
          board.updateCard({ id });
          this.showTasksOn3d(this.board.getCard(id));
        };

        // Cross-asset isolation handler for task 3D button
        window.handleTaskIsolateClick = async (id) => {
          await this.handleTaskIsolation(this.board.getCard(id));
        };

        // Start Verification handler — only wired on Verification Boards (gated at template level) [AVA-337]
        window.handleStartVerificationClick = (id) => {
          const task = this.board.getCard(id);
          if (!task) return;
          if (!task.forgeTagsIds || !task.forgeTagsIds.length) {
            this.$dialog.notify.warning('Link at least one tag to this task before verifying.');
            return;
          }
          this.$router.push({
            path: `/${this.$route.params.id}/verification/${id}`,
          });
        };

        // Right-click on the isolate button: open the facility selection dialog
        // ONLY when there are other facilities to navigate to. Mirrors
        // TagCard.handleIsolateRightClick — silent when nothing to navigate.
        window.handleTaskIsolateRightClick = async (id) => {
          const task = this.board.getCard(id);
          if (!task) return;
          let info = this.taskIsolationMap[task.id];
          if (!info) {
            // Defensive: prefetch may not have resolved yet.
            info = await getTaskIsolationInfo(task, this.$store);
            if (info) this.$set(this.taskIsolationMap, task.id, info);
          }
          debugLog('Kanban', 'handleTaskIsolateRightClick', {
            taskId: id,
            otherFacilitiesCount: info?.otherAssets?.length ?? 0,
          });
          if (info?.otherAssets?.length > 0) {
            await this.showFacilitySelectionDialog(task, info);
          }
          // No-op when no other facilities — matches TagCard semantics.
        };

        window.showStatusIn3D = (statusId) => {
          this.lastIsolatedStatusId = statusId;
          const tasks = this.entries.filter((e) => e.statusId == statusId);
          this.showTasksOn3d(tasks);
        };

        // Share-intent: deep-link copy on a task card. Bridges raw HTML
        // onclick / oncontextmenu attributes → Vue methods → SHARE_INTENT
        // registry encoder. Uses event + element pair so the handler can
        // read the task id from data-task-id (XSS-safe pattern, same as
        // renderIsolateButtonHTML) AND the click coordinates (for menu
        // positioning).
        window.handleTaskCopyLinkClick = (event, el) => {
          const taskId = el?.dataset?.taskId;
          if (!taskId) return;
          this.handleTaskCopyLink(taskId, event);
        };
        window.handleTaskCopyLinkRightClick = (event, el) => {
          const taskId = el?.dataset?.taskId;
          if (!taskId) return;
          this.handleTaskCopyLinkRightClick(taskId, event);
        };
      },
      cardTemplate({ cardFields, selected, dragging, cardShape }) {
        function hexToRgb(hex) {
          const bigint = parseInt(hex.slice(1), 16);
          const r = (bigint >> 16) & 255;
          const g = (bigint >> 8) & 255;
          const b = bigint & 255;
          return `${r}, ${g}, ${b}`;
        }

        const {
          id,
          text,
          comments,
          end_date,
          status,
          forgeTagsIds,
          color,
          participants,
          projectId,
          progress,
        } = cardFields;
        const progressValue = Math.max(0, Math.min(100, Number(progress) || 0));
        const { backgroundColor, labelColor } = this.getColorForDueDate(cardFields);
        // const formattedDate = end_date ? dayjs(end_date).format('MMM DD') : null;
        const formattedDate = end_date ? dayjs(end_date).format('DD/MM/YYYY') : null;
        const selectedTask = this.isolatedTasks.includes(id);
        const isHighlighted = this.highlightedTaskId && this.highlightedTaskId == id;

        /* Date color
          If it's the same date or passed the date - keep it red
          If it's one week ahead, keep it yellow
          if it's longer than a week, keep it black
        */
        const dateDiff = end_date ? dayjs(end_date).diff(dayjs(), 'day') : null;
        let dateClass = 'date-black';
        /* The `end_date` guard is load-bearing — do NOT collapse it back into a
           bare `if (dateDiff <= 0)`. When a task has no due date `dateDiff` is
           null, and `null <= 0` is TRUE in JS (null coerces to 0), so an undated
           card used to land on 'date-red'. That was near-invisible while only the
           "No Due Date" pill was tinted, but `dateClass` now also colours the
           progress bar — and a red bar on a task that is not late is a lie. */
        if (end_date) {
          if (dateDiff <= 0) {
            dateClass = 'date-red';
          } else if (dateDiff <= 7) {
            dateClass = 'date-orange';
          }
        }

        /* The progress bar borrows the date's urgency colour, EXCEPT once the
           work is done. `dateClass` knows nothing about completion, so a card
           sitting in Done past its due date would paint a full RED bar — which
           reads as a live problem instead of a delivered task.
           The due-date chip deliberately keeps `dateClass`: there the red still
           says something true, that it WAS delivered late. Only the bar, which
           reads as current status, goes neutral. */
        const progressClass = this.isCardCompleted(cardFields) ? 'date-black' : dateClass;

        let statusColor = status?.color || '#000000';
        if (/^#[0-9A-Fa-f]{8}$/.test(statusColor)) {
          // If it includes an alpha channel (8 characters), remove the alpha part
          statusColor = statusColor.slice(0, 7);
        } else if (!/^#[0-9A-Fa-f]{6}$/.test(statusColor)) {
          // If it's not a valid 6-character hex code, default to black
          statusColor = '#000000';
        }

        const project = this.projects.find((p) => p.id === projectId)?.description;

        const isVerificationBoard = !!this.currentTaskType?.isVerificationBoard;

        const rgbColor = hexToRgb(statusColor);

        return `
            <div class="kanban-card ${isHighlighted ? 'kanban-card-highlighted' : ''}" ${
          color ? `style="box-shadow: 0 1px 4px ${color}40; border-left: 4px solid ${color};"` : ''
        }>
              <!-- Card Color -->
             <!--  <div class="wx-color wx-rounded svelte-kqkezg" style="background: ${
               status?.color
             };"></div> -->

              <div class="wx-content svelte-kqkezg">
                <!-- <div class="wx-card-header svelte-upffav"></div> -->

                <!-- Card Body -->
                <div class="wx-body svelte-kqkezg">
                  <div class="wx-field wx-label svelte-kqkezg">
                      <span>${text}</span>
                      <div class="wx-menu svelte-kqkezg d-flex flex-row">
                        ${this.renderCopyLinkButtonHTML(id)}
                        <div data-menu-id="${id}" data-ignore-selection="true">
                          <i class="wx-icon svelte-p36u8h wx-clickable card-menu-icon mdi mdi-dots-horizontal"></i>
                        </div>
                      </div>
                  </div>
                </div>

                <!-- Project -->
                ${project ? `<span class="project-name">${project}</span>` : ''}

                <!-- Due Date (body metadata, above the progress bar) -->
                <div class="card-date-row">
                  <div class="wx-icons-container svelte-vhwr63 ${dateClass}">
                      <div class="wx-date svelte-vhwr63 due-date-container">
                        <i class="wx-icon wxi-clock svelte-p36u8h due-date-icon"></i>
                        ${
                          formattedDate
                            ? `<span class="wx-date-value svelte-vhwr63 due-date-text">Due date: ${formattedDate}</span>`
                            : 'No Due Date'
                        }
                      </div>
                  </div>
                </div>

                <!-- Progress row (directly above the footer; hidden entirely when progress is 0) -->
                ${
                  progressValue > 0
                    ? `<div class="card-progress-row ${progressClass}" title="Progress: ${progressValue}%">
                  <div class="card-progress-bar-track">
                    <div class="card-progress-bar-fill" style="width: ${progressValue}%;"></div>
                  </div>
                  <span class="card-progress-label">${progressValue}%</span>
                </div>`
                    : ''
                }

                <!-- Card Footer (actions LEFT | info RIGHT).
                     There is deliberately NO .wx-card-icons wrapper here: .wx-footer
                     splits its own DIRECT children, so wrapping these two clusters
                     would silently collapse the split back into a stacked column. -->
                <div class="wx-footer svelte-vhwr63 wx-with-content">
                      <!-- LEFT cluster: action icons -->
                      <div class="card-footer-actions">
                        <!-- 3D / Cross-Asset Isolation -->
                        ${this.renderIsolateButtonHTML(id, forgeTagsIds)}

                        <!-- Start Verification (visible only on Verification Boards; requires ≥1 linked tag) [AVA-337] -->
                        ${
                          isVerificationBoard && forgeTagsIds?.length
                            ? `<div class="wx-show-verify svelte-vhwr63" data-ignore-selection="true" onclick="handleStartVerificationClick('${id}')" title="Start Verification">
                            <i class="wx-icon svelte-p36u8h mdi mdi-shield-check-outline" style="font-size: 22px;"></i>
                          </div>`
                            : ''
                        }
                      </div>

                      <!-- RIGHT cluster: avatars + comment count -->
                      <div class="card-footer-info">
                        <!-- Participants Avatars (hidden entirely when no valid participants,
                             so a card with no assignees renders no phantom empty container) -->
                        ${
                          participants && participants.filter((u) => !!u).length > 0
                            ? `<div class="wx-users d-flex participants-container">
                            ${participants
                              .map((user, index) => {
                                if (!user) return '';

                                // Calculate margin-left to create overlapping effect but prevent complete overlap
                                const marginLeft = index > 0 ? '-8px' : '0';

                                if (user.avatarUrl) {
                                  return `<div class="user-avatar" style="margin-left: ${marginLeft}; z-index: ${
                                    10 - index
                                  };">
                                  <img src="${user.avatarUrl}" class="avatar-image" />
                                </div>`;
                                } else {
                                  // Extract first two characters from firstName as fallback
                                  const initials = user.firstName
                                    ? user.firstName.substring(0, 2).toUpperCase()
                                    : 'NA';
                                  // Generate deterministic color based on user ID or name
                                  const seed = user.id || user.firstName + (user.lastName || '');
                                  // Simple hash function to convert string to number
                                  let hash = 0;
                                  for (let i = 0; i < seed.toString().length; i++) {
                                    hash = seed.toString().charCodeAt(i) + ((hash << 5) - hash);
                                  }
                                  // Convert hash to HSL color values
                                  const hue = Math.abs(hash % 360);
                                  const saturation = 70 + (Math.abs(hash) % 30);
                                  const lightness = 15 + (Math.abs(hash) % 25);
                                  const bgColor = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
                                  return `<div title="${user.firstName} ${
                                    user.lastName
                                  }" class="user-avatar user-avatar-initials" style="margin-left: ${marginLeft}; background-color: ${bgColor}; z-index: ${
                                    10 - index
                                  };">
                                  ${initials}
                                </div>`;
                                }
                              })
                              .join('')}
                          </div>`
                            : ''
                        }

                        <!-- Comments -->
                        <div class="wx-comments svelte-vhwr63">
                          <i class="wx-icon svelte-p36u8h comment-icon">
                                  <svg viewBox="0 0 14 15" width="22" height="22" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                            <g clip-path="url(#clip0_427_3813)">
                            <path d="M1.40349 1.36797C1.44902 1.36782 1.44902 1.36782 1.49546 1.36766C1.59672 1.36737 1.69797 1.36748 1.79922 1.36759C1.872 1.36747 1.94478 1.36733 2.01756 1.36717C2.19447 1.36682 2.37138 1.36677 2.54829 1.36683C2.69217 1.36688 2.83605 1.36683 2.97993 1.36672C3.00044 1.3667 3.02095 1.36669 3.04209 1.36667C3.08377 1.36664 3.12545 1.3666 3.16713 1.36657C3.55764 1.36627 3.94814 1.36633 4.33865 1.3665C4.69541 1.36664 5.05217 1.36633 5.40893 1.36581C5.77577 1.36528 6.14261 1.36506 6.50945 1.36516C6.7152 1.36521 6.92094 1.36514 7.12668 1.36476C7.30186 1.36443 7.47703 1.36439 7.6522 1.36472C7.74146 1.36488 7.83071 1.36487 7.91997 1.36459C8.01701 1.3643 8.11405 1.36452 8.21109 1.36487C8.23891 1.36467 8.26672 1.36447 8.29538 1.36426C8.6895 1.3672 9.00154 1.47759 9.2879 1.74638C9.60844 2.07982 9.65702 2.45165 9.65501 2.89524C9.65496 2.94394 9.65496 2.94394 9.6549 2.99362C9.65474 3.09669 9.6544 3.19976 9.65405 3.30284C9.65392 3.37298 9.65379 3.44311 9.65368 3.51325C9.65338 3.68465 9.65292 3.85605 9.65234 4.02745C9.66825 4.02737 9.68415 4.02729 9.70054 4.02722C10.0888 4.0254 10.4771 4.02404 10.8653 4.02319C11.0531 4.02277 11.2409 4.0222 11.4286 4.02126C11.61 4.02035 11.7914 4.01987 11.9727 4.01966C12.0418 4.01951 12.1108 4.01922 12.1798 4.01877C12.2769 4.01817 12.3739 4.01809 12.471 4.01813C12.5133 4.01769 12.5133 4.01769 12.5565 4.01724C12.9798 4.01901 13.3296 4.16052 13.6366 4.45191C13.9125 4.75036 14.0172 5.09388 14.0137 5.49416C14.0139 5.52738 14.014 5.56059 14.0142 5.59482C14.0145 5.68605 14.0143 5.77726 14.0139 5.86849C14.0136 5.96724 14.0139 6.06598 14.0142 6.16473C14.0145 6.35775 14.0143 6.55077 14.0139 6.74379C14.0135 6.9687 14.0136 7.19362 14.0137 7.41853C14.0138 7.81979 14.0134 8.22104 14.0128 8.6223C14.0121 9.01151 14.0119 9.40071 14.0122 9.78991C14.0125 10.2142 14.0126 10.6384 14.0122 11.0627C14.0121 11.1079 14.0121 11.1532 14.012 11.1984C14.012 11.2207 14.012 11.2429 14.012 11.2658C14.0118 11.4226 14.0119 11.5793 14.012 11.736C14.0121 11.9269 14.012 12.1179 14.0114 12.3088C14.0112 12.4061 14.011 12.5035 14.0113 12.6009C14.0115 12.7064 14.0111 12.812 14.0106 12.9175C14.0108 12.9637 14.0108 12.9637 14.0111 13.0108C14.0096 13.1888 13.9962 13.3176 13.8739 13.4563C13.7449 13.5642 13.6417 13.6161 13.4719 13.6354C13.2445 13.6122 13.0722 13.4857 12.8994 13.3448C12.8567 13.3109 12.8139 13.2769 12.7711 13.243C12.7488 13.2252 12.7265 13.2074 12.7035 13.1891C12.5788 13.09 12.453 12.9925 12.3269 12.8954C12.3031 12.8767 12.2792 12.858 12.2546 12.8388C12.2205 12.8125 12.2205 12.8125 12.1856 12.7857C12.1557 12.7625 12.1557 12.7625 12.1252 12.7388C11.932 12.613 11.7767 12.6016 11.5502 12.603C11.5025 12.6028 11.5025 12.6028 11.4538 12.6027C11.3659 12.6024 11.278 12.6025 11.1901 12.6028C11.0952 12.6029 11.0003 12.6026 10.9053 12.6024C10.7409 12.6021 10.5765 12.602 10.4121 12.6022C10.1741 12.6023 9.93604 12.602 9.69802 12.6015C9.31143 12.6007 8.92484 12.6002 8.53825 12.6C8.51513 12.6 8.492 12.6 8.46817 12.6C8.30406 12.5999 8.13995 12.5998 7.97583 12.5997C7.7885 12.5996 7.60118 12.5995 7.41385 12.5994C7.39078 12.5994 7.3677 12.5994 7.34393 12.5994C6.95886 12.5991 6.57379 12.5986 6.18873 12.5978C5.95139 12.5973 5.71406 12.5971 5.47672 12.5973C5.31401 12.5974 5.1513 12.5973 4.98859 12.5969C4.8948 12.5966 4.80101 12.5966 4.70722 12.5968C4.60536 12.5971 4.50352 12.5968 4.40166 12.5964C4.37227 12.5966 4.34287 12.5968 4.31259 12.597C3.91888 12.594 3.59306 12.446 3.30859 12.1759C3.06351 11.8818 2.94372 11.5834 2.94692 11.2017C2.94691 11.1763 2.9469 11.1509 2.94689 11.1248C2.94693 11.0416 2.94735 10.9585 2.94778 10.8753C2.94789 10.8174 2.94796 10.7595 2.94802 10.7016C2.94823 10.5496 2.94876 10.3977 2.94937 10.2457C2.94993 10.0905 2.95018 9.93524 2.95045 9.77999C2.95104 9.47576 2.95197 9.17152 2.95313 8.86729C2.80085 8.8685 2.6486 8.87041 2.49634 8.87296C2.4446 8.87373 2.39286 8.87431 2.34112 8.8747C2.2665 8.87529 2.19193 8.87656 2.11733 8.87797C2.09439 8.878 2.07145 8.87804 2.04782 8.87807C1.82868 8.88347 1.69851 8.97171 1.53296 9.11338C1.40534 9.21999 1.27479 9.32144 1.1416 9.421C1.12352 9.43453 1.10544 9.44807 1.08681 9.46201C1.03458 9.50095 0.982175 9.53963 0.929688 9.57823C0.912223 9.59118 0.894757 9.60413 0.876763 9.61748C0.696421 9.74938 0.548072 9.76654 0.328126 9.74229C0.181462 9.68962 0.0995832 9.60926 0.0236226 9.47278C-0.0216875 9.36034 -0.0149823 9.24501 -0.0138883 9.12535C-0.0140589 9.09659 -0.0142296 9.06783 -0.0144054 9.0382C-0.014841 8.94199 -0.0144963 8.84581 -0.014152 8.74961C-0.0143002 8.68056 -0.014495 8.61152 -0.0147328 8.54248C-0.0151798 8.37449 -0.0151143 8.2065 -0.0147971 8.03851C-0.0145498 7.90189 -0.0145167 7.76527 -0.0146356 7.62865C-0.0146523 7.60918 -0.0146691 7.58972 -0.0146864 7.56966C-0.0147214 7.53011 -0.0147568 7.49056 -0.0147928 7.45101C-0.0151045 7.08027 -0.0147463 6.70953 -0.0141584 6.33879C-0.0136701 6.02111 -0.0137542 5.70343 -0.0142588 5.38574C-0.0148451 5.01647 -0.015074 4.6472 -0.0147387 4.27794C-0.0147039 4.2385 -0.0146695 4.19906 -0.0146356 4.15963C-0.0146187 4.14022 -0.0146019 4.12082 -0.0145845 4.10083C-0.0144903 3.96453 -0.0146489 3.82823 -0.0149068 3.69193C-0.0152469 3.50816 -0.0150022 3.32439 -0.0143715 3.14062C-0.0142272 3.07329 -0.0142678 3.00595 -0.0145096 2.93861C-0.0148099 2.84646 -0.0144318 2.75433 -0.0138883 2.66218C-0.0141323 2.63574 -0.0143763 2.6093 -0.0146277 2.58205C-0.0112881 2.2887 0.0940441 2.04219 0.273438 1.8126C0.286515 1.79575 0.299592 1.7789 0.313065 1.76155C0.589656 1.44803 1.00604 1.36651 1.40349 1.36797ZM0.765625 2.22276C0.750962 2.2408 0.736299 2.25885 0.721192 2.27745C0.590468 2.46759 0.597541 2.67448 0.598275 2.8978C0.598251 2.92398 0.598228 2.95016 0.598204 2.97714C0.598159 3.06459 0.598309 3.15204 0.598459 3.23949C0.598466 3.30229 0.598462 3.36508 0.598447 3.42788C0.598439 3.5982 0.5986 3.76851 0.59879 3.93883C0.598961 4.11688 0.598978 4.29493 0.59901 4.47299C0.599085 4.77178 0.599256 5.07058 0.5995 5.36938C0.599751 5.67708 0.599945 5.98479 0.600061 6.2925C0.600068 6.31148 0.600075 6.33046 0.600083 6.35001C0.600118 6.44523 0.600153 6.54046 0.600187 6.63568C0.60047 7.42512 0.600954 8.21456 0.601563 9.00401C0.641142 8.97776 0.680704 8.95149 0.720257 8.9252C0.753306 8.90326 0.753306 8.90326 0.787022 8.88087C0.850657 8.83792 0.911647 8.79286 0.971665 8.74499C1.0023 8.7208 1.0023 8.7208 1.03356 8.69612C1.07494 8.66317 1.11632 8.6302 1.15768 8.59722C1.45182 8.36511 1.67732 8.25927 2.05591 8.2544C2.0817 8.25387 2.10748 8.25334 2.13405 8.2528C2.21567 8.25118 2.29729 8.2499 2.37891 8.24864C2.43456 8.24759 2.4902 8.24652 2.54585 8.24543C2.68161 8.24282 2.81736 8.24052 2.95313 8.23838C2.95317 8.22293 2.95321 8.20747 2.95325 8.19155C2.95426 7.81399 2.95589 7.43645 2.95815 7.0589C2.95924 6.87631 2.96011 6.69373 2.96054 6.51114C2.96095 6.33473 2.9619 6.15832 2.9632 5.98192C2.9636 5.91482 2.96382 5.84772 2.96384 5.78063C2.96419 5.27069 2.9844 4.85045 3.34192 4.45202C3.72007 4.0843 4.16 4.04668 4.65892 4.04607C4.70664 4.04582 4.75436 4.04554 4.80209 4.04524C4.93093 4.04449 5.05978 4.04408 5.18862 4.04372C5.32354 4.04329 5.45846 4.04256 5.59337 4.04186C5.84854 4.04059 6.1037 4.03958 6.35887 4.03867C6.6495 4.03762 6.94013 4.03627 7.23076 4.0349C7.82832 4.03208 8.42588 4.02964 9.02344 4.02745C9.02553 3.82556 9.02696 3.62368 9.02794 3.42179C9.02835 3.35318 9.02891 3.28457 9.02963 3.21596C9.03063 3.11707 9.03109 3.01818 9.03145 2.91928C9.03187 2.88885 9.0323 2.85842 9.03273 2.82706C9.03279 2.55722 8.9877 2.35774 8.8002 2.15878C8.66872 2.03988 8.52212 2.00072 8.34942 2.00034C8.32385 2.00022 8.29827 2.00009 8.27192 1.99997C8.22991 1.99998 8.22991 1.99998 8.18705 1.99998C8.14231 1.99983 8.14231 1.99983 8.09667 1.99968C7.99637 1.99938 7.89608 1.99927 7.79579 1.99917C7.72402 1.999 7.65225 1.99881 7.58048 1.99862C7.36485 1.99808 7.14923 1.99781 6.9336 1.99756C6.832 1.99744 6.73041 1.9973 6.62881 1.99715C6.29099 1.99669 5.95316 1.99629 5.61534 1.9961C5.5277 1.99604 5.44006 1.99599 5.35242 1.99594C5.33064 1.99592 5.30886 1.99591 5.28641 1.9959C4.93367 1.99567 4.58093 1.99498 4.2282 1.99409C3.86587 1.99318 3.50355 1.99269 3.14122 1.9926C2.93786 1.99255 2.7345 1.99231 2.53114 1.99161C2.35793 1.99101 2.18472 1.99079 2.0115 1.99106C1.92319 1.99118 1.83489 1.99107 1.74658 1.9906C1.65064 1.99012 1.55473 1.99031 1.45879 1.99067C1.43115 1.99037 1.4035 1.99007 1.37502 1.98976C1.15037 1.99179 0.911387 2.03618 0.765625 2.22276ZM3.70219 4.97978C3.5914 5.15233 3.56996 5.31492 3.57106 5.51454C3.57088 5.53951 3.57069 5.56447 3.5705 5.5902C3.56998 5.6734 3.57005 5.75659 3.57012 5.8398C3.56987 5.89964 3.56959 5.95948 3.56928 6.01933C3.56856 6.18159 3.56836 6.34384 3.56831 6.5061C3.56825 6.60757 3.56808 6.70904 3.56786 6.81051C3.56714 7.14649 3.56676 7.48248 3.56682 7.81847C3.56682 7.83655 3.56683 7.85464 3.56683 7.87327C3.56683 7.89138 3.56684 7.90949 3.56684 7.92815C3.56687 8.2032 3.56623 8.47824 3.56515 8.75329C3.56389 9.07323 3.5632 9.39317 3.56326 9.71311C3.56328 9.88227 3.56305 10.0514 3.56218 10.2206C3.56138 10.3798 3.56138 10.5389 3.56197 10.6981C3.56206 10.7563 3.56187 10.8146 3.56137 10.8728C3.55457 11.318 3.55457 11.318 3.74609 11.711C3.96738 11.9278 4.16515 11.9653 4.46669 11.9644C4.49811 11.9645 4.52952 11.9646 4.56189 11.9647C4.64886 11.965 4.73582 11.9651 4.82279 11.965C4.91668 11.965 5.01057 11.9653 5.10446 11.9655C5.26712 11.966 5.42978 11.9662 5.59244 11.9663C5.82794 11.9665 6.06345 11.967 6.29895 11.9676C6.68149 11.9686 7.06403 11.9693 7.44657 11.9699C7.48091 11.97 7.48091 11.97 7.51593 11.97C7.67835 11.9703 7.84076 11.9706 8.00317 11.9708C8.18846 11.9711 8.37375 11.9714 8.55904 11.9717C8.59327 11.9717 8.59327 11.9717 8.62819 11.9718C9.00915 11.9724 9.39011 11.9732 9.77106 11.9742C10.0059 11.9748 10.2406 11.9752 10.4754 11.9753C10.6365 11.9755 10.7975 11.9758 10.9585 11.9762C11.0513 11.9765 11.1441 11.9766 11.2369 11.9766C11.3377 11.9765 11.4386 11.9768 11.5394 11.9772C11.5684 11.9771 11.5974 11.977 11.6273 11.977C12.0336 11.9795 12.3027 12.0713 12.6157 12.3246C12.7924 12.4648 12.9711 12.601 13.1541 12.733C13.1754 12.7485 13.1967 12.7639 13.2187 12.7798C13.3015 12.8512 13.3015 12.8512 13.3984 12.8868C13.3997 11.9405 13.4007 10.9942 13.4012 10.0479C13.4013 9.93607 13.4014 9.82429 13.4014 9.71251C13.4015 9.69026 13.4015 9.66801 13.4015 9.64508C13.4017 9.285 13.4022 8.92491 13.4027 8.56483C13.4032 8.19522 13.4035 7.8256 13.4036 7.45599C13.4037 7.22801 13.4039 7.00003 13.4043 6.77205C13.4046 6.61561 13.4047 6.45917 13.4046 6.30274C13.4046 6.21255 13.4047 6.12236 13.405 6.03217C13.4052 5.93419 13.4052 5.83623 13.405 5.73825C13.4052 5.71 13.4053 5.68176 13.4055 5.65265C13.4043 5.3513 13.367 5.10375 13.1581 4.8766C12.9327 4.66198 12.6927 4.64422 12.3914 4.64593C12.3393 4.64574 12.3393 4.64574 12.2861 4.64555C12.1901 4.64521 12.0942 4.64537 11.9983 4.64563C11.8946 4.64581 11.7909 4.64553 11.6873 4.6453C11.4844 4.64494 11.2815 4.64502 11.0786 4.64525C10.9137 4.64544 10.7488 4.64546 10.5839 4.64537C10.5604 4.64536 10.5369 4.64535 10.5127 4.64534C10.4649 4.64531 10.4172 4.64528 10.3695 4.64526C9.92212 4.64502 9.47478 4.64529 9.02743 4.64573C8.64382 4.6461 8.2602 4.64603 7.87659 4.64566C7.43081 4.64522 6.98504 4.64505 6.53926 4.6453C6.4917 4.64532 6.44414 4.64535 6.39658 4.64537C6.37318 4.64539 6.34978 4.6454 6.32568 4.64541C6.16101 4.64548 5.99634 4.64536 5.83167 4.64517C5.63092 4.64494 5.43016 4.645 5.22941 4.64544C5.12704 4.64566 5.02467 4.64574 4.9223 4.64547C4.81121 4.64518 4.70014 4.64553 4.58906 4.64593C4.54076 4.64566 4.54076 4.64566 4.49147 4.64538C4.16205 4.64773 3.92561 4.73113 3.70219 4.97978Z" fill="currentColor"/>
                            <path d="M1.40349 1.36797C1.44902 1.36782 1.44902 1.36782 1.49546 1.36766C1.59672 1.36737 1.69797 1.36748 1.79922 1.36759C1.872 1.36747 1.94478 1.36733 2.01756 1.36717C2.19447 1.36682 2.37138 1.36677 2.54829 1.36683C2.69217 1.36688 2.83605 1.36683 2.97993 1.36672C3.00044 1.3667 3.02095 1.36669 3.04209 1.36667C3.08377 1.36664 3.12545 1.3666 3.16713 1.36657C3.55764 1.36627 3.94815 1.36633 4.33865 1.3665C4.69542 1.36664 5.05217 1.36633 5.40893 1.36581C5.77577 1.36528 6.14261 1.36506 6.50945 1.36516C6.7152 1.36521 6.92094 1.36514 7.12669 1.36476C7.30186 1.36443 7.47703 1.36439 7.6522 1.36472C7.74146 1.36488 7.83071 1.36487 7.91997 1.36459C8.01701 1.3643 8.11405 1.36452 8.21109 1.36487C8.23891 1.36467 8.26672 1.36447 8.29538 1.36426C8.6895 1.3672 9.00154 1.47759 9.2879 1.74638C9.60804 2.0794 9.65676 2.45124 9.65598 2.89428C9.65602 2.94288 9.65602 2.94288 9.65606 2.99246C9.65607 3.06055 9.65602 3.12865 9.65591 3.19674C9.65576 3.30144 9.65591 3.40615 9.65608 3.51085C9.65606 3.57706 9.65603 3.64326 9.65598 3.70947C9.65603 3.74094 9.65609 3.77241 9.65614 3.80483C9.65606 3.83369 9.65597 3.86254 9.65588 3.89227C9.65585 3.93053 9.65585 3.93053 9.65582 3.96957C9.65234 4.02745 9.65234 4.02745 9.625 4.05479C9.5803 4.05717 9.5355 4.05771 9.49074 4.05746C9.46369 4.05737 9.43663 4.05728 9.40876 4.05719C9.3803 4.05696 9.35184 4.05673 9.32251 4.0565C9.29394 4.05637 9.26537 4.05625 9.23594 4.05612C9.1651 4.0558 9.09427 4.05535 9.02344 4.05479C9.0232 4.02786 9.0232 4.02786 9.02295 4.00039C9.0212 3.81274 9.01889 3.62509 9.01626 3.43745C9.01536 3.36752 9.01459 3.29758 9.01396 3.22765C9.01304 3.12684 9.01161 3.02605 9.01009 2.92526C9.00982 2.87872 9.00982 2.87872 9.00956 2.83123C9.00494 2.57918 8.96989 2.36846 8.79411 2.18003C8.66502 2.06501 8.51752 2.02805 8.34942 2.02768C8.32385 2.02756 8.29827 2.02744 8.27192 2.02731C8.22991 2.02732 8.22991 2.02732 8.18705 2.02733C8.14231 2.02718 8.14231 2.02718 8.09667 2.02702C7.99637 2.02672 7.89608 2.02662 7.79579 2.02651C7.72402 2.02634 7.65225 2.02616 7.58048 2.02597C7.36485 2.02543 7.14923 2.02515 6.9336 2.02491C6.832 2.02479 6.73041 2.02464 6.62881 2.0245C6.29099 2.02403 5.95316 2.02364 5.61534 2.02344C5.5277 2.02339 5.44006 2.02333 5.35242 2.02328C5.33064 2.02327 5.30886 2.02325 5.28641 2.02324C4.93367 2.02301 4.58094 2.02232 4.2282 2.02143C3.86587 2.02053 3.50355 2.02004 3.14122 2.01995C2.93786 2.01989 2.7345 2.01965 2.53114 2.01895C2.35793 2.01836 2.18472 2.01814 2.0115 2.0184C1.92319 2.01853 1.83489 2.01842 1.74658 2.01795C1.65064 2.01746 1.55473 2.01766 1.45879 2.01801C1.43115 2.01771 1.4035 2.01741 1.37502 2.01711C1.17517 2.01891 0.97439 2.04994 0.821739 2.18938C0.631163 2.40065 0.624714 2.62285 0.625619 2.89676C0.625584 2.93587 0.625584 2.93587 0.625548 2.97576C0.625503 3.06283 0.625653 3.14991 0.625803 3.23698C0.62581 3.29951 0.625806 3.36203 0.62579 3.42456C0.625783 3.59414 0.625943 3.76373 0.626134 3.93331C0.626305 4.11059 0.626321 4.28788 0.626354 4.46517C0.626429 4.76268 0.6266 5.06019 0.626844 5.3577C0.627095 5.66408 0.627289 5.97046 0.627405 6.27684C0.627412 6.29573 0.627419 6.31463 0.627427 6.3341C0.627462 6.42892 0.627497 6.52373 0.627531 6.61854C0.627814 7.40458 0.628298 8.19062 0.628907 8.97666C0.642582 8.96596 0.656256 8.95526 0.670345 8.94423C0.733617 8.89476 0.796938 8.84535 0.860261 8.79594C0.881759 8.77911 0.903258 8.76229 0.925409 8.74495C1.58886 8.22768 1.58886 8.22768 2.05591 8.23304C2.0817 8.23312 2.10749 8.2332 2.13405 8.23328C2.21568 8.23358 2.29729 8.23427 2.37891 8.23496C2.43456 8.23524 2.4902 8.23549 2.54585 8.23571C2.68162 8.23627 2.81736 8.23734 2.95313 8.23838C2.95313 8.44592 2.95313 8.65346 2.95313 8.86729C2.83802 8.86835 2.83802 8.86835 2.7206 8.86942C2.64584 8.87048 2.57109 8.87171 2.49634 8.87296C2.4446 8.87373 2.39286 8.87431 2.34112 8.8747C2.2665 8.87529 2.19193 8.87656 2.11733 8.87797C2.09439 8.878 2.07145 8.87804 2.04782 8.87807C1.82868 8.88347 1.69851 8.97171 1.53296 9.11338C1.40534 9.21999 1.27479 9.32144 1.1416 9.421C1.12352 9.43453 1.10544 9.44807 1.08681 9.46202C1.03458 9.50095 0.982175 9.53963 0.929688 9.57823C0.912223 9.59118 0.894757 9.60413 0.876763 9.61748C0.696421 9.74938 0.548072 9.76654 0.328126 9.74229C0.181462 9.68962 0.0995832 9.60926 0.0236226 9.47278C-0.0216875 9.36034 -0.0149823 9.24501 -0.0138883 9.12535C-0.0140589 9.09659 -0.0142296 9.06783 -0.0144054 9.0382C-0.014841 8.94199 -0.0144963 8.84581 -0.014152 8.74961C-0.0143002 8.68056 -0.014495 8.61152 -0.0147328 8.54248C-0.0151798 8.37449 -0.0151143 8.2065 -0.0147971 8.03851C-0.0145498 7.90189 -0.0145167 7.76527 -0.0146356 7.62865C-0.0146523 7.60918 -0.0146691 7.58972 -0.0146864 7.56966C-0.0147214 7.53011 -0.0147568 7.49056 -0.0147928 7.45101C-0.0151045 7.08027 -0.0147463 6.70953 -0.0141584 6.33879C-0.0136701 6.02111 -0.0137542 5.70343 -0.0142588 5.38574C-0.0148451 5.01647 -0.015074 4.6472 -0.0147387 4.27794C-0.0147039 4.2385 -0.0146695 4.19906 -0.0146356 4.15963C-0.0146187 4.14022 -0.0146019 4.12082 -0.0145845 4.10083C-0.0144903 3.96453 -0.0146489 3.82823 -0.0149068 3.69193C-0.0152469 3.50816 -0.0150022 3.3244 -0.0143715 3.14062C-0.0142272 3.07329 -0.0142678 3.00595 -0.0145096 2.93861C-0.0148099 2.84646 -0.0144318 2.75433 -0.0138883 2.66218C-0.0141323 2.63574 -0.0143763 2.6093 -0.0146277 2.58205C-0.0112881 2.2887 0.0940441 2.04219 0.273438 1.8126C0.286515 1.79575 0.299592 1.7789 0.313065 1.76155C0.589656 1.44803 1.00604 1.36651 1.40349 1.36797Z" fill="currentColor"/>
                            <path d="M4.8307 6.86747C4.87025 6.86732 4.87025 6.86732 4.9106 6.86716C4.93963 6.86718 4.96867 6.8672 4.99858 6.86723C5.04477 6.86711 5.04477 6.86711 5.09189 6.86698C5.19561 6.86674 5.29933 6.8667 5.40305 6.86666C5.47719 6.86655 5.55133 6.8664 5.62548 6.86625C5.82728 6.86588 6.02907 6.86571 6.23087 6.86559C6.35684 6.86552 6.48281 6.8654 6.60877 6.86528C6.95743 6.86495 7.30609 6.86466 7.65475 6.86457C7.67707 6.86457 7.69939 6.86456 7.72238 6.86455C7.75594 6.86454 7.75594 6.86454 7.79017 6.86454C7.83551 6.86452 7.88084 6.86451 7.92618 6.8645C7.94866 6.86449 7.97115 6.86449 7.99432 6.86448C8.35882 6.86437 8.72333 6.8639 9.08783 6.86326C9.46193 6.86261 9.83602 6.86227 10.2101 6.86224C10.4202 6.86221 10.6303 6.86205 10.8404 6.86156C11.0193 6.86114 11.1981 6.861 11.377 6.86122C11.4683 6.86133 11.5596 6.86126 11.6509 6.86092C11.7498 6.86058 11.8487 6.86073 11.9476 6.86102C11.9765 6.8608 12.0054 6.86058 12.0352 6.86035C12.2075 6.86152 12.3052 6.88003 12.4363 6.99753C12.4875 7.09998 12.4901 7.19664 12.4688 7.30857C12.4217 7.39248 12.3646 7.45632 12.2773 7.49997C12.2359 7.5026 12.1943 7.50351 12.1527 7.50357C12.1131 7.50373 12.1131 7.50373 12.0727 7.50389C12.0289 7.50385 12.0289 7.50385 11.9841 7.50381C11.9378 7.50394 11.9378 7.50394 11.8906 7.50406C11.7864 7.5043 11.6822 7.50434 11.578 7.50438C11.5036 7.50449 11.4292 7.50464 11.3548 7.50479C11.1521 7.50516 10.9495 7.50533 10.7468 7.50545C10.6204 7.50552 10.4939 7.50564 10.3674 7.50576C10.0174 7.5061 9.66731 7.50638 9.31727 7.50647C9.28365 7.50648 9.28365 7.50648 9.24935 7.50649C9.21565 7.5065 9.21565 7.5065 9.18126 7.50651C9.13573 7.50652 9.0902 7.50653 9.04467 7.50654C9.02209 7.50655 8.9995 7.50656 8.97623 7.50656C8.61023 7.50667 8.24422 7.50715 7.87821 7.50778C7.50263 7.50843 7.12706 7.50877 6.75148 7.5088C6.54053 7.50883 6.32958 7.50899 6.11862 7.50948C5.939 7.5099 5.75938 7.51004 5.57975 7.50982C5.48808 7.50971 5.39642 7.50978 5.30475 7.51012C5.20541 7.51047 5.10608 7.51031 5.00674 7.51002C4.96319 7.51035 4.96319 7.51035 4.91876 7.51069C4.74607 7.50952 4.64817 7.49125 4.51685 7.37351C4.46558 7.27097 4.46325 7.1745 4.48438 7.06247C4.56549 6.91268 4.66573 6.86772 4.8307 6.86747Z" fill="currentColor"/>
                            <path d="M4.83398 5.4695C4.87359 5.46923 4.87359 5.46923 4.914 5.46896C4.94293 5.46905 4.97186 5.46913 5.00168 5.46922C5.03247 5.4691 5.06327 5.46897 5.095 5.46884C5.19857 5.4685 5.30214 5.46854 5.40571 5.46859C5.47981 5.46845 5.55392 5.46824 5.62802 5.46803C5.80864 5.46758 5.98926 5.46743 6.16988 5.4674C6.31668 5.46737 6.46349 5.46726 6.61029 5.46708C7.02644 5.4666 7.44259 5.46635 7.85874 5.46639C7.89239 5.46639 7.89239 5.46639 7.92673 5.4664C7.94919 5.4664 7.97166 5.4664 7.99481 5.4664C8.35894 5.46643 8.72306 5.4659 9.08719 5.46513C9.46101 5.46435 9.83483 5.46397 10.2086 5.46402C10.4185 5.46403 10.6284 5.46388 10.8383 5.4633C11.017 5.4628 11.1957 5.46268 11.3744 5.46305C11.4656 5.46323 11.5568 5.46317 11.648 5.46276C11.7468 5.46234 11.8456 5.46259 11.9445 5.46301C11.9877 5.46259 11.9877 5.46259 12.0318 5.46216C12.1951 5.4637 12.2904 5.47597 12.4141 5.58591C12.4707 5.67094 12.4762 5.6923 12.4756 5.78928C12.4757 5.81011 12.4759 5.83094 12.476 5.8524C12.4644 5.95071 12.4384 6.00279 12.3625 6.067C12.2342 6.12491 12.1126 6.11692 11.9734 6.11622C11.9424 6.11639 11.9113 6.11656 11.8793 6.11673C11.7751 6.11718 11.6709 6.11705 11.5666 6.11692C11.492 6.11708 11.4173 6.11735 11.3427 6.11762C11.1608 6.1182 10.979 6.11832 10.7972 6.11827C10.6494 6.11824 10.5016 6.11834 10.3538 6.11854C10.3112 6.1186 10.2686 6.11866 10.2261 6.11872C10.2049 6.11874 10.1837 6.11877 10.1619 6.1188C9.76117 6.11932 9.36047 6.11929 8.95977 6.1191C8.59317 6.11895 8.22657 6.11952 7.85996 6.12043C7.48355 6.12135 7.10714 6.12176 6.73072 6.12164C6.51939 6.12158 6.30805 6.12172 6.09672 6.1224C5.91682 6.12297 5.73692 6.12307 5.55702 6.12256C5.46522 6.12231 5.37346 6.12235 5.28167 6.12284C5.18215 6.12333 5.08268 6.12299 4.98317 6.12243C4.95416 6.12277 4.92516 6.12312 4.89527 6.12347C4.76882 6.12194 4.67723 6.12077 4.57624 6.03943C4.47993 5.93389 4.47501 5.86834 4.47657 5.72967C4.48821 5.63748 4.52633 5.59301 4.59374 5.53122C4.67657 5.48117 4.73773 5.46966 4.83398 5.4695Z" fill="currentColor"/>
                            <path d="M4.92658 8.2613C4.96116 8.26109 4.96116 8.26109 4.99643 8.26088C5.07395 8.26045 5.15146 8.26021 5.22898 8.25998C5.28445 8.25972 5.33992 8.25945 5.39538 8.25917C5.57778 8.25831 5.76017 8.25776 5.94257 8.25725C5.97358 8.25716 6.0046 8.25707 6.03656 8.25698C6.32901 8.25614 6.62147 8.25541 6.91392 8.25494C7.28817 8.25434 7.66241 8.2532 8.03666 8.25129C8.29972 8.25 8.56278 8.24934 8.82584 8.24914C8.983 8.24901 9.14015 8.24859 9.29731 8.24751C9.44518 8.2465 9.59302 8.24627 9.74088 8.24663C9.79513 8.24663 9.84937 8.24635 9.90361 8.24576C9.97774 8.24501 10.0518 8.24527 10.1259 8.24578C10.1473 8.24532 10.1688 8.24486 10.1908 8.24438C10.3371 8.24689 10.412 8.2886 10.5189 8.38772C10.5876 8.46843 10.5773 8.53701 10.5702 8.63729C10.5449 8.74504 10.4997 8.79315 10.418 8.86725C10.3154 8.90143 10.22 8.89855 10.1131 8.89844C10.0902 8.89852 10.0672 8.8986 10.0435 8.89868C9.96626 8.89892 9.88899 8.89896 9.81172 8.899C9.75643 8.89913 9.70115 8.89927 9.64586 8.89941C9.49547 8.89977 9.34508 8.89995 9.19469 8.90007C9.10079 8.90015 9.00689 8.90026 8.91298 8.90038C8.61934 8.90076 8.32569 8.90102 8.03205 8.90113C7.69278 8.90125 7.35351 8.90173 7.01425 8.90252C6.75219 8.90311 6.49013 8.90339 6.22806 8.90343C6.07146 8.90345 5.91485 8.90362 5.75825 8.9041C5.61097 8.90455 5.4637 8.90461 5.31643 8.90437C5.26234 8.90435 5.20825 8.90447 5.15416 8.90474C5.08039 8.90509 5.00663 8.90493 4.93286 8.90464C4.91139 8.90486 4.88993 8.90509 4.86782 8.90531C4.7149 8.90392 4.62915 8.86882 4.51685 8.76813C4.46558 8.6656 4.46325 8.56912 4.48438 8.4571C4.58784 8.26602 4.72813 8.26159 4.92658 8.2613Z" fill="currentColor"/>
                            <path d="M4.83631 10.4724C4.8553 10.4723 4.8743 10.4721 4.89387 10.4719C4.95668 10.4714 5.01949 10.4713 5.08231 10.4712C5.126 10.471 5.1697 10.4708 5.21339 10.4706C5.30502 10.4703 5.39664 10.4702 5.48826 10.4702C5.60556 10.4703 5.72284 10.4695 5.84013 10.4686C5.9304 10.468 6.02066 10.4678 6.11093 10.4679C6.15417 10.4678 6.1974 10.4676 6.24064 10.4671C6.30119 10.4666 6.3617 10.4668 6.42225 10.4671C6.45667 10.467 6.49109 10.4669 6.52656 10.4669C6.63323 10.4828 6.67759 10.5152 6.75391 10.5898C6.81649 10.6792 6.82149 10.7557 6.8086 10.8632C6.75125 10.9802 6.69415 11.0444 6.57243 11.093C6.42296 11.1307 6.26625 11.121 6.11304 11.1202C6.07454 11.1202 6.03603 11.1203 5.99753 11.1204C5.91709 11.1205 5.83665 11.1204 5.75621 11.12C5.65351 11.1195 5.55083 11.1198 5.44813 11.1203C5.3687 11.1206 5.28927 11.1205 5.20984 11.1203C5.17199 11.1202 5.13414 11.1203 5.09629 11.1205C4.72611 11.1219 4.72611 11.1219 4.56641 11.0273C4.48278 10.9328 4.47154 10.8465 4.47637 10.7246C4.48819 10.6467 4.51781 10.5963 4.56641 10.5351C4.66682 10.4681 4.71651 10.4732 4.83631 10.4724Z" fill="currentColor"/>
                            </g>
                            <defs>
                            <clipPath id="clip0_427_3813">
                            <rect width="14" height="14" fill="white" transform="translate(0 0.5)"/>
                            </clipPath>
                            </defs>
                            </svg>

                          </i>
                          <span class="wx-item-value svelte-vhwr63">${comments?.length}</span>
                        </div>
                      </div>
                </div>
              </div>
            </div>
          `;
      },
      /**
       * Is this card's work finished?
       *
       * Extracted so the due-date chip and the progress bar cannot drift apart
       * on what "done" means — they read completion from one place. The board
       * carries two spellings of the status name, and both are live data.
       */
      isCardCompleted(card) {
        const name = card?.currentTaskStatus?.name;
        return name === 'Completed' || name === 'Complete';
      },
      getColorForDueDate(card) {
        const { end_date } = card;

        const isCompleted = this.isCardCompleted(card);

        const today = dayjs();
        const daysUntilDue = dayjs(end_date).diff(today, 'day');

        let backgroundColor, labelColor;

        if (isCompleted) {
          backgroundColor = '#03624c'; // Completed task
          labelColor = '#FFFFFF';
        } else if (daysUntilDue <= 0) {
          backgroundColor = '#A80000'; // Past due
          labelColor = '#FFFFFF';
        } else if (daysUntilDue <= 3) {
          backgroundColor = '#C04F15'; // Due within 3 days
          labelColor = '#FFFFFF';
        } else {
          backgroundColor = ''; // No color change
          labelColor = this.$vuetify.theme.isDark ? '#FFFFFF' : '#000000';
        }

        return { backgroundColor, labelColor };
      },
      onEditTask(payLoad) {
        payLoad.task.label = payLoad.task.text;
        if (payLoad.isNew) {
          if (payLoad.id)
            this.board.updateCard({ id: payLoad.id, card: payLoad.task, replace: true });
          else {
            this.board.addCard(payLoad.task);
          }
        } else {
          this.board.updateCard({ id: payLoad.task.id, card: payLoad.task, replace: true });
        }
        this.$emit('edit-completed', payLoad);

        if (this.lastIsolatedStatusId) {
          showStatusIn3D(this.lastIsolatedStatusId);
        }

        this.debounceStatus3DUpdate();
      },
      /**
       * Handle cross-asset isolation for a task.
       * Determines behavior based on where the task's tags are harvested.
       *
       * Reads from taskIsolationMap when available (populated by
       * prefetchTaskIsolations on entries change). Falls back to fetching if
       * the map entry is missing — covers the race where a user clicks before
       * the prefetch resolves, or the entry was evicted on a list refresh.
       */
      async handleTaskIsolation(task) {
        try {
          let isolationInfo = this.taskIsolationMap[task.id];
          if (!isolationInfo) {
            isolationInfo = await getTaskIsolationInfo(task, this.$store);
            if (isolationInfo) this.$set(this.taskIsolationMap, task.id, isolationInfo);
            debugLog('Kanban', 'handleTaskIsolation: fell back to fetch', {
              taskId: task.id,
              hasInfo: !!isolationInfo,
            });
          }
          const behavior = getIsolateClickBehavior(isolationInfo);

          switch (behavior) {
            case 'isolate':
              // Tags ONLY in current facility - isolate directly
              this.lastIsolatedStatusId = null;
              this.board.updateCard({ id: task.id });
              this.showTasksOn3d(task);
              break;

            case 'navigate-menu':
              // Other facilities exist - show selection dialog (let user decide)
              this.showFacilitySelectionDialog(task, isolationInfo);
              break;

            default:
              // Fallback to basic 3D behavior
              this.showTasksOn3d(task);
          }
        } catch (err) {
          error('Kanban', 'Failed to handle task isolation', err);
          // Fallback to basic behavior on error
          this.showTasksOn3d(task);
        }
      },

      /**
       * Show facility selection dialog when tags exist in multiple facilities.
       */
      async showFacilitySelectionDialog(task, isolationInfo) {
        const result = await this.$refs.facilityDialog.open(task, isolationInfo, this.facilityDict);

        if (result.cancelled) return;

        if (result.isCurrent) {
          // User chose to isolate in current facility
          this.lastIsolatedStatusId = null;
          this.board.updateCard({ id: task.id });
          this.showTasksOn3d(task);
        } else {
          // User chose to open in another facility — pass isolationInfo so
          // navigateToFacility picks the right viewer URL contract (3D vs
          // 360) based on where the tag actually exists at the destination.
          navigateToFacility(
            this.$router,
            result.facilityId,
            getTaskEffectiveTagIds(task, isolationInfo),
            isolationInfo,
          );
        }
      },

      showTasksOn3d(tasks) {
        this.$store.commit('SET_VIEWER_TYPE', '3d');
        this.$emit('showIn3d', tasks);
      },

      /**
       * Pre-fetch isolation info for every task with linked tags. Phase 2:
       * a single batched API call (POST /api/tasks/getIsolationByIds) replaces
       * the per-task fetch loop. The shared module-level map inside
       * taskIsolation.js gets populated once; we then mirror the relevant
       * entries into the local reactive taskIsolationMap so cardTemplate (a
       * sync HTML-string template) can read them on each render without async.
       */
      async prefetchTaskIsolations(tasks) {
        if (!tasks?.length) return;

        const persisted = tasks.filter((task) => task?.id);
        if (!persisted.length) return;

        // Drop stale local map entries for tasks no longer on the board.
        const validIds = new Set(persisted.map((task) => task.id));
        Object.keys(this.taskIsolationMap).forEach((id) => {
          if (!validIds.has(id)) {
            this.$delete(this.taskIsolationMap, id);
            this.$delete(this.taskIsolationLoading, id);
          }
        });

        // Mark every tagged task as loading so cardTemplate renders the
        // skeleton until the batch resolves.
        persisted.forEach((task) => {
          if (!this.taskIsolationMap[task.id]) {
            this.$set(this.taskIsolationLoading, task.id, true);
          }
        });

        // ONE network call for the entire board.
        try {
          await prefetchTasksIsolation(persisted, this.$store);
        } catch (err) {
          error('Kanban', 'prefetchTasksIsolation failed', err);
        }

        // Mirror results from the global per-task-id map into our local
        // reactive map so cardTemplate (sync) can read them. getTaskIsolationInfo
        // now hits the global map for free (no network) — we just need the
        // values in a Vue-reactive container so updateCard re-renders correctly.
        await Promise.allSettled(
          persisted.map(async (task) => {
            try {
              const info = await getTaskIsolationInfo(task, this.$store);
              if (info) this.$set(this.taskIsolationMap, task.id, info);
              this.board.updateCard({ id: task.id });
            } catch (err) {
              error('Kanban', 'mirror to local map failed', { taskId: task.id, err });
            } finally {
              this.$set(this.taskIsolationLoading, task.id, false);
            }
          }),
        );

        debugLog('Kanban', 'prefetchTaskIsolations complete (batched)', {
          taskCount: persisted.length,
          mapSize: Object.keys(this.taskIsolationMap).length,
        });
      },

      /**
       * HTML-escape attribute values for safe interpolation into the
       * cardTemplate HTML string. Tooltip text incorporates user-controlled
       * facility names, so guard against quote/angle-bracket injection.
       */
      escapeAttr(s) {
        if (s == null) return '';
        return String(s)
          .replace(/&/g, '&amp;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');
      },

      /**
       * Build the isolate-button HTML segment for a card. Three branches:
       *  1. No forgeTagsIds → return '' (no button).
       *  2. Loading (no info yet, fetch in flight) → return a skeleton div
       *     with no click handlers (reserves the slot, no flicker).
       *  3. Info loaded → return the full button. Hidden again if the
       *     aggregate has nothing to isolate (no children + not harvested).
       *     Cross-facility orange via .wx-show-3d--cross-facility class.
       *
       * Icon stays mdi-loupe to preserve the task card's visual identity;
       * only coloring/visibility/tooltip align with TagCard.
       */
      renderIsolateButtonHTML(taskId, forgeTagsIds) {
        const info = this.taskIsolationMap[taskId];
        const isLoading = this.taskIsolationLoading[taskId];

        // Branch 2: in-flight, render skeleton — hand-drawn isometric cube
        // SVG with a synchronized opacity "breathing" pulse on every edge.
        // See CSS in the <style> block at end of file.
        if (!info && isLoading) {
          return `<div class="wx-show-3d wx-show-3d--loading svelte-vhwr63" data-ignore-selection="true" aria-hidden="true"><svg viewBox="0 0 24 24" class="wx-show-3d-cube" xmlns="http://www.w3.org/2000/svg"><g fill="none" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><polygon points="12,2 20.5,7 20.5,17 12,22 3.5,17 3.5,7" class="wx-show-3d-cube__edge"/><polyline points="12,12 12,2" class="wx-show-3d-cube__edge"/><polyline points="12,12 20.5,7" class="wx-show-3d-cube__edge"/><polyline points="12,12 3.5,7" class="wx-show-3d-cube__edge"/></g></svg></div>`;
        }

        // No info available (and not loading) → hide entirely.
        if (!info) return '';

        // Aggregate has nothing to isolate (no children + not harvested) → hide.
        const { combinedAssets, hasCurrentFacility } = info;
        if (!combinedAssets?.length && !hasCurrentFacility) return '';

        // Branch 3: render the real button.
        const colorClass =
          getIsolateButtonColor(info) === 'warning' ? 'wx-show-3d--cross-facility' : '';
        const tooltip = this.escapeAttr(getIsolateTooltip(info, this.facilityDict));
        // Pick the icon based on whether the click will open the 3D viewer
        // (mdi-cube-outline — harvested data) or the 360 viewer
        // (mdi-image-filter-center-focus — positioned-only data).
        const iconClass = this.escapeAttr(getIsolateIcon(info));
        // Pass the task id via data attribute (HTML-attribute-encoded) and
        // read it inside the handler via this.dataset.taskId. Avoids raw
        // string interpolation into onclick/oncontextmenu JS bodies, which
        // would break or inject for any non-GUID id (e.g. the 'temp' + counter
        // ids generated by optimistic add-card at Kanban.vue:242).
        const taskIdAttr = this.escapeAttr(taskId);

        return `<div class="wx-show-3d svelte-vhwr63 ${colorClass}" data-ignore-selection="true" data-task-id="${taskIdAttr}" title="${tooltip}" onclick="handleTaskIsolateClick(this.dataset.taskId)" oncontextmenu="event.preventDefault(); handleTaskIsolateRightClick(this.dataset.taskId); return false;"><i class="wx-icon svelte-p36u8h mdi ${iconClass}" style="font-size: 24px;"></i></div>`;
      },

      /**
       * Renders the copy-link icon for a task card. Three branches:
       *   1. No isolation data yet (in-flight prefetch) → loading skeleton
       *      (link-chain SVG with synchronized opacity-pulse animation,
       *      matching the isolate skeleton's "thinking" affordance).
       *   2. Cross-facility (combinedAssets includes facilities other than
       *      current) → orange icon, click opens the centralized menu.
       *   3. Single-target (only current facility, with or without tags) →
       *      blue icon, click directly copies the right URL via the
       *      universal rule (task-expand if has tags, task-board if not).
       *
       * Always rendered (no `forgeTagsIds.length` gate) per the universal
       * rule: a task with no tags anywhere still gets a button that copies
       * a `task-board` URL for the current facility, so the recipient can
       * at least see the task on the Kanban.
       */
      renderCopyLinkButtonHTML(taskId) {
        const info = this.taskIsolationMap[taskId];
        const isLoading = this.taskIsolationLoading[taskId];

        // Branch 1: skeleton during in-flight prefetch.
        if (!info && isLoading) {
          return `<div class="wx-copy-link wx-copy-link--loading card-menu-icon" data-ignore-selection="true" aria-hidden="true" style="display:flex;align-items:center;margin-right:4px;"><svg viewBox="0 0 24 24" class="wx-copy-link-skeleton" xmlns="http://www.w3.org/2000/svg" style="width:18px;height:18px;"><g fill="none" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M9.5 14.5 a3 3 0 0 1 0-4.2 l3-3 a3 3 0 0 1 4.2 4.2 l-1.5 1.5" class="wx-copy-link-skeleton__edge"/><path d="M14.5 9.5 a3 3 0 0 1 0 4.2 l-3 3 a3 3 0 0 1-4.2-4.2 l1.5-1.5" class="wx-copy-link-skeleton__edge"/></g></svg></div>`;
        }

        // Branch 2 / 3: real button. Color reflects single-vs-multi target.
        const taskIdAttr = this.escapeAttr(taskId);
        const isCrossFacility =
          info && getCopyLinkButtonColor(info) === 'warning';
        const colorClass = isCrossFacility ? 'wx-copy-link--cross-facility' : '';

        return `<div class="wx-copy-link card-menu-icon ${colorClass}" data-ignore-selection="true" data-task-id="${taskIdAttr}" title="Copy link to task" style="display:flex;align-items:center;cursor:pointer;margin-right:4px;" onclick="handleTaskCopyLinkClick(event, this)" oncontextmenu="event.preventDefault(); handleTaskCopyLinkRightClick(event, this); return false;"><i class="mdi mdi-link-variant" style="font-size:18px;"></i></div>`;
      },

      /**
       * Left-click handler. Routes through getCopyLinkBehavior:
       *   - 'direct-copy' → write the right URL (universal rule) to clipboard
       *   - 'navigate-menu' → open the centralized menu at click coords
       *   - 'none' → defensive no-op (button shouldn't have rendered)
       */
      handleTaskCopyLink(taskId, event) {
        const info = this.taskIsolationMap[taskId] || null;
        const behavior = getCopyLinkBehavior(info);
        debugLog('Kanban', 'handleTaskCopyLink', {
          taskId,
          behavior,
        });
        if (behavior === 'navigate-menu') {
          this.openCopyLinkMenu(taskId, event);
          return;
        }
        if (behavior === 'direct-copy' || behavior === 'none') {
          // 'none' shouldn't happen (button is always rendered post-load),
          // but treat it as direct-copy of the universal-rule fallback —
          // safer than silently dropping a click.
          const target = getCopyLinkDirectTarget(info, this.$store.state.assetId);
          if (!target) {
            this.$dialog?.notify?.error?.('Cannot copy link — current facility unknown');
            return;
          }
          copyTaskLinkToClipboard({
            facilityId: target.facilityId,
            taskId,
            taskTypeId: this.$route.params.taskTypeId,
            intent: target.intent,
            dialog: this.$dialog,
          });
        }
      },

      /**
       * Right-click handler. Always opens the menu when there's at least
       * one item to show; matches TaskCopyLinkButton parity.
       */
      handleTaskCopyLinkRightClick(taskId, event) {
        debugLog('Kanban', 'handleTaskCopyLinkRightClick', { taskId });
        this.openCopyLinkMenu(taskId, event);
      },

      /**
       * Populates and shows the centralized copy-link menu, anchored at the
       * click event's viewport coordinates.
       */
      openCopyLinkMenu(taskId, event) {
        const info = this.taskIsolationMap[taskId] || null;
        const items = getCopyLinkMenuItems(
          info,
          this.$store.state.assetId,
          this.facilityDict,
        );
        if (items.length === 0) {
          // No menu items means we have no current asset id AND no isolation
          // info — shouldn't reach here, but bail safely instead of opening
          // an empty menu.
          debugLog('Kanban', 'openCopyLinkMenu: no items — bailing', { taskId });
          return;
        }
        this.copyLinkMenu.taskId = taskId;
        this.copyLinkMenu.items = items;
        this.copyLinkMenu.x = event?.clientX ?? 0;
        this.copyLinkMenu.y = event?.clientY ?? 0;
        // Vuetify needs a tick to react to coords if the menu was already
        // open at a different location; close-then-open prevents stuck-pos.
        this.copyLinkMenu.show = false;
        this.$nextTick(() => {
          this.copyLinkMenu.show = true;
        });
      },

      /**
       * Click handler for a chosen menu item — copies the corresponding URL
       * via the shared helper, picking the right encoder by item.intent.
       */
      pickCopyLinkFacility(item) {
        const taskId = this.copyLinkMenu.taskId;
        if (!taskId) return;
        debugLog('Kanban', 'pickCopyLinkFacility', {
          taskId,
          intent: item.intent,
          facilityId: item.facilityId,
        });
        copyTaskLinkToClipboard({
          facilityId: item.facilityId,
          taskId,
          taskTypeId: this.$route.params.taskTypeId,
          intent: item.intent,
          dialog: this.$dialog,
        });
        this.copyLinkMenu.show = false;
      },
      checkDarkMode() {
        if (this.board?.config) {
          this.board?.setConfig({
            theme: { name: this.$vuetify.theme.isDark ? 'willow-dark' : 'willow' },
          });
          this.debounceStatus3DUpdate();
        }
      },
      adjustKanbantHeight() {
        const kanbanRoot = document.querySelector('#kanbanContainer');
        if (kanbanRoot) kanbanRoot.style.height = `${window.innerHeight / 2}px`;
      },
      async setupStatus3DOptions() {
        const success = await retryAddStatus3dOption({
          taskType: this.taskType,
          entries: this.entries,
          maxRetries: 200,
          retryDelay: 100,
        });

        if (!success) {
          error('Kanban', 'Failed to add 3D status options after multiple attempts');
        }
      },

      debounceStatus3DUpdate() {
        if (this.status3dUpdateTimeout) {
          clearTimeout(this.status3dUpdateTimeout);
        }

        this.status3dUpdateTimeout = setTimeout(() => {
          if (this.$route.name === 'kanban') {
            this.setupStatus3DOptions();
          }
        }, 100);
      },
      updateHighlightedTask() {
        // Get taskId from route query parameters
        const taskId = this.$route.query.taskId;
        this.highlightedTaskId = taskId;

        // Auto-clear highlighting after 5 seconds
        // if (taskId) {
        //   setTimeout(() => {
        //     this.highlightedTaskId = null;
        //     this.board.parse(this.kanbanData);
        //   }, 5000);
        // }

        // Trigger kanban re-render to apply highlighting
        if (this.board && this.board.parse) {
          this.reloadBoardFromStore();
        }
      },
    },
    components: {
      EditTask,
      FacilitySelectDialog,
    },
    activated() {
      this.checkDarkMode();
      this.configEvents();
      this.attachBoardKeyboard();
    },
    deactivated() {
      this.eventsInitialized = false;
      this.detachBoardKeyboard();
    },
    watch: {
      entries() {
        this.reloadBoardFromStore();

        if (this.isolatedTasks.length) {
          this.showTasksOn3d(this.entries.filter((e) => this.isolatedTasks.includes(e.id)));
        }

        this.debounceStatus3DUpdate();
        // Pre-fetch isolation info so cardTemplate can render visibility/color
        // synchronously once info is available. isolationCache de-dupes across
        // tasks that share the same tag set.
        this.prefetchTaskIsolations(this.entries);
      },
      '$route.query.taskId': {
        handler(newTaskId, oldTaskId) {
          if (newTaskId !== oldTaskId) {
            this.updateHighlightedTask();
          }
        },
        immediate: true,
      },
      currentTaskType: {
        handler(taskType, old) {
          if (taskType && taskType.id !== old?.id) {
            this.reloadBoardFromStore();
            this.setupStatus3DOptions();
            this.prefetchTaskIsolations(this.entries);
          }
        },
      },
      projects: {
        handler: function () {
          this.reloadBoardFromStore();
          this.prefetchTaskIsolations(this.entries);
        },
      },
      isolatedTasks(value, old) {
        let set = new Set([...value, ...old]);
        set.forEach((id) => {
          this.board.updateCard({ id });
        });
      },
      '$vuetify.theme.isDark': {
        handler: function () {
          this.checkDarkMode();
          this.configEvents(true);
        },
      },
    },
    beforeDestroy() {
      this.detachBoardKeyboard();
      // Clean up the window-globals registered in initKanban() so the closures
      // (which captured this destroyed component instance) don't linger on the
      // window object — prevents stale-closure bugs if the component remounts.
      delete window.handleTaskCopyLinkClick;
      delete window.handleTaskCopyLinkRightClick;
      delete window.handleTaskIsolateClick;
      delete window.handleTaskIsolateRightClick;
      delete window.handleStartVerificationClick;
      if (this.status3dUpdateTimeout) {
        clearTimeout(this.status3dUpdateTimeout);
      }
    },
  };
</script>

<style lang="scss">
  .wx-willow-theme,
  .wx-willow-dark-theme {
    height: 100%;
    padding-right: 15px;
    padding-bottom: 5px;
  }
  .wx-willow-dark-theme {
    background: transparent;
  }
  .wx-willow-theme .wx-kanban {
    --wx-kanban-background: transparent !important;
  }

  .wx-willow-dark-theme .wx-kanban {
    --wx-kanban-background: var(--v-bg2-base);
    --wx-kanban-shadow: 0 0 0 1px #4e4e4e, 0px 0px 0px 0 rgba(0, 0, 0, 0.1);
  }

  .wx-card.svelte-1t9rqgm {
    border-top: none;
    background: var(--v-kanban_card_bg_color-base);
    /* The card body now stacks four rows (title, project, due date, progress)
       above the footer, so neighbouring cards need their own gutter or they
       read as one continuous block. !important beats the vendor's inline
       margin on .wx-card. */
    margin: 6px 0 !important;
  }

  .wx-show-3d {
    cursor: pointer;
  }

  .wx-show-3d:hover i {
    color: var(--v-primary-base);
  }

  // ─── Start Verification button (green) [AVA-337] ───
  .wx-show-verify {
    cursor: pointer;
    margin-left: 4px;
  }

  .wx-show-verify i {
    color: var(--v-success-base);
  }

  .wx-show-verify:hover i {
    color: var(--v-success-darken1);
  }

  // ─── Cross-Facility Orange (mirrors TagCard.vue:820-833) ───
  // Applied when getIsolateButtonColor(info) === 'warning'. !important is
  // necessary because the generic .wx-show-3d:hover i rule above also targets
  // the icon color.
  .wx-show-3d--cross-facility i {
    color: #d97706 !important;
    transition: all 0.2s ease;
  }

  .wx-show-3d--cross-facility:hover i {
    color: #b45309 !important;
    transform: scale(1.1);
  }

  // ─── Loading Skeleton (cube outline + breathing pulse) ───
  // Reserves the slot during the prefetch so the card layout doesn't shift
  // when the button finally appears (or stays hidden). Hand-drawn isometric
  // cube SVG: every edge shares a synchronized opacity "breathing" pulse.
  // Reads as "thinking" without competing for attention across many cards.
  // GPU-cheap (only opacity animates).
  .wx-show-3d--loading {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    cursor: default;
    pointer-events: none;
  }

  .wx-show-3d-cube {
    width: 22px;
    height: 22px;
    overflow: visible;
  }

  .wx-show-3d-cube__edge {
    stroke: var(--v-primary-base);
    animation: kanban-isolate-cube-breathe 1.6s ease-in-out infinite;
  }

  @keyframes kanban-isolate-cube-breathe {
    0%,
    100% {
      opacity: 0.25;
    }
    50% {
      opacity: 0.5;
    }
  }

  // Accessibility: pin to a midpoint when the user prefers reduced motion.
  @media (prefers-reduced-motion: reduce) {
    .wx-show-3d-cube__edge {
      animation: none;
      opacity: 0.6;
    }
  }

  // ─── Copy-link skeleton + cross-facility orange coloring ───
  // Skeleton: hand-drawn link-chain SVG with the same synchronized opacity
  // breathing pulse as the isolate skeleton. Reads as "thinking" while
  // staying out of the user's way; only opacity animates so it's free on
  // the GPU compositor.
  .wx-copy-link-skeleton {
    overflow: visible;
  }

  .wx-copy-link-skeleton__edge {
    stroke: var(--v-primary-base);
    animation: kanban-copy-link-breathe 1.6s ease-in-out infinite;
  }

  @keyframes kanban-copy-link-breathe {
    0%,
    100% {
      opacity: 0.25;
    }
    50% {
      opacity: 0.6;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .wx-copy-link-skeleton__edge {
      animation: none;
      opacity: 0.6;
    }
  }

  // Cross-facility orange tint — applied when the task's tags span multiple
  // facilities; communicates "this click opens a menu, not a direct copy."
  // Mirrors .wx-show-3d--cross-facility's coloring rules so users learn one
  // mental model across both buttons.
  .wx-copy-link--cross-facility .mdi {
    color: #d97706 !important;
    transition: color 0.2s ease;
  }

  .wx-copy-link--cross-facility:hover .mdi {
    color: #b45309 !important;
  }

  // ─── Copy-link menu (centralized v-menu in <template>) ───
  // Same visual language as TaskCopyLinkButton.vue's inline menu so users
  // see consistent UI whether they're in Kanban / List / Gantt.
  .copy-link-menu {
    min-width: 240px;
    max-width: 360px;
    border-radius: 8px !important;
    overflow: hidden;
    border: 1px solid rgba(217, 119, 6, 0.2);

    &__list {
      padding: 0 !important;
    }

    &__header {
      background: linear-gradient(135deg, rgba(217, 119, 6, 0.08) 0%, rgba(217, 119, 6, 0.04) 100%);
      font-weight: 600;
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #d97706 !important;
      padding: 12px 16px !important;
      height: auto !important;
    }

    &__item {
      transition: all 0.15s ease;
      min-height: 48px;

      &:hover {
        background: rgba(217, 119, 6, 0.08) !important;
      }

      &:active {
        background: rgba(217, 119, 6, 0.15) !important;
      }
    }

    &__name {
      font-weight: 500;
      font-size: 13px;
      color: inherit;
    }

    &__intent {
      font-size: 11px;
      opacity: 0.7;
    }
  }

  .theme--dark {
    .copy-link-menu {
      border-color: rgba(217, 119, 6, 0.3);

      &__header {
        background: linear-gradient(
          135deg,
          rgba(217, 119, 6, 0.15) 0%,
          rgba(217, 119, 6, 0.08) 100%
        );
      }

      &__item:hover {
        background: rgba(217, 119, 6, 0.12) !important;
      }
    }
  }

  .wx-selected-card {
    box-shadow: var(--v-primary-lighten2) 0px 2px 2px 0px, var(--v-primary-lighten2) 0px 2px 8px 0px;
  }

  .wx-label.svelte-ms9wpd.svelte-ms9wpd {
    display: flex;
    justify-content: space-between;
    /* margin-right: 20px; */
  }
  /* .wx-column.svelte-pinuaq.svelte-pinuaq {
    max-height: 100%;
    overflow: auto;
  } */
  .wx-column.svelte-pinuaq.svelte-pinuaq {
    background: var(--v-light-grey2-base);
    box-shadow: 0px 0px 10px 0px #0000000d;
    padding: 0px 10px;
    margin-left: 0px;
    border: 1px solid var(--v-bg7-base);
    border-top: none;
  }
  .wx-column.svelte-pinuaq.svelte-pinuaq:not(:first-child) {
    margin-left: 12px;
  }
  .wx-header.svelte-bteu5p {
    padding: 0px;
  }
  .wx-column.svelte-ms9wpd.svelte-ms9wpd {
    margin-left: 0px;
    background: var(--v-light-grey2-base);
    border: none;
    border-radius: 0px;
    border-top-right-radius: 3px;
    border-top-left-radius: 3px;
    padding: 0 var(--wx-padding);
    box-shadow: 0px -10px 10px -10px #0000000d, 10px 0px 10px -10px #0000000d,
      -10px 0px 10px -10px #0000000d;
    padding-top: 25px;
    padding-bottom: 5px;
    border: 1px solid var(--v-bg7-base);
    border-bottom: none;
  }
  .wx-column.svelte-ms9wpd.svelte-ms9wpd:not(:first-child) {
    margin-left: 12px;
  }
  .wx-icon.svelte-p36u8h {
    color: var(--v-normal-grey-base);
  }
  .wx-icon svg {
    fill: var(--v-normal-grey-base); /* Assuming this variable is defined */
  }
  .wx-content.svelte-kqkezg.svelte-kqkezg {
    padding: 10px;
    /* 4px, not 10px: this gap is applied BETWEEN every stacked row of the card
       body. With the due-date row and the progress row restored there are now
       up to five rows, and a 10px gutter on each made the card twice as tall.
       The rows carry their own padding (.card-date-row / .card-progress-row). */
    gap: 4px;
  }
  .wx-card.svelte-1t9rqgm {
    border-radius: 5px;
    box-shadow: none;
    border: none;
  }
  .wx-content.svelte-1837yaa.svelte-1837yaa {
    height: 100%;
    padding-bottom: 10px;
  }
  .wx-menu.svelte-ms9wpd.svelte-ms9wpd {
    top: calc(65% - 10px);
    display: none;
  }
  .wx-add-card-tip.svelte-pinuaq.svelte-pinuaq {
    opacity: 1;
    color: var(--v-normal-grey-base);
    font-weight: 600;
  }
  .wx-list-wrapper.svelte-z1lwbw {
    overflow: auto;
    flex-grow: 1;
    margin-bottom: 10px;
  }
  .wx-row.svelte-30nbk4 {
    height: 100% !important;
  }
  .wx-content.svelte-30nbk4 {
    height: 100%;
  }
  .wx-column.svelte-pinuaq {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  .modal .svelte-1a9hi0k {
    display: none;
  }

  .wx-with-content.svelte-vhwr63 {
    padding-top: 0;
  }

  .kanban-card {
    border-radius: 6px;
    border: 1px solid #0000004a;
  }

  .card-menu-icon {
    font-size: 20px;
  }

  .participants-container {
    /* No margin-right here on purpose. The avatars now live inside
       .card-footer-info, whose `margin-left: auto` owns the right alignment;
       a margin here would push them off the flush-right edge. */
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
  }

  .user-avatar {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    overflow: hidden;
    margin-right: 0px;
    border: 1px solid white;
    position: relative;
  }

  .avatar-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .user-avatar-initials {
    background-color: var(--avatar-bg, #333);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 600;
    border: 1px solid var(--v-bg1-base);
  }

  .due-date-container {
    padding: 2px;
    border-radius: 4px;
    // color: #A80000 !important;
    font-weight: 600;
    background: #a800000d;
  }

  .due-date-icon {
    font-size: 15px;
    color: inherit;
  }

  .due-date-text {
    font-size: 11px;
  }

  .comment-icon {
    font-size: 20px;
  }

  .wx-list-wrapper.svelte-1tfu1im {
    overflow: auto;
    flex-grow: 1;
    margin-bottom: 10px;
  }

  .wx-label.collapsable.svelte-30nbk4 {
    display: none !important;
  }

  .project-name {
    background: var(--v-primary-base);
    width: fit-content;
    padding: 0px 8px;
    border-radius: 14px;
    color: white;
    font-size: 10px;
    font-weight: 600;
  }

  .date-red {
    .due-date-container {
      color: #f53636 !important;
      background: #a800000d;
    }
  }

  .date-orange {
    .due-date-container {
      color: #ffa500 !important;
      background: #ffa5000d;
    }
  }

  .date-black {
    .due-date-container {
      color: #5a5a5a !important;
      background: #3535350d;
    }
  }

  .date-red .due-date-icon {
    color: #f53636 !important;
  }

  .date-orange .due-date-icon {
    color: #ffa500 !important;
  }

  .date-black .due-date-icon {
    color: #5a5a5a !important;
  }

  .kanban-card-highlighted {
    border-left: 4px solid var(--v-primary-base) !important;
    border-top: 1px solid var(--v-primary-base) !important;
    border-right: 1px solid var(--v-primary-base) !important;
    border-bottom: 1px solid var(--v-primary-base) !important;
    box-shadow: 0 2px 8px var(--v-primary-base) 15, 0 1px 3px rgba(0, 0, 0, 0.1) !important;
    background: var(--v-primary-base) 04 !important;
    position: relative !important;
    transition: all 0.2s ease-in-out !important;
  }

  /* ─── Task card: due-date row + progress row + split footer [AVA-337] ─── */

  /* Progress row (directly above the footer): thick bar + % label on the right.
     Bottom padding is 0 — the gap between this row and the footer comes from
     .wx-content's `gap` plus this row's own top padding. (.wx-with-content
     zeroes the footer's padding-top, so the footer contributes nothing.) */
  .card-progress-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 4px 0 0 0;
  }

  .card-progress-bar-track {
    flex: 1 1 auto;
    height: 8px;
    border-radius: 4px;
    background: var(--v-light-grey-base);
    overflow: hidden;
  }

  .card-progress-bar-fill {
    height: 100%;
    /* background is overridden by .date-red / .date-orange / .date-black below */
    background: #5a5a5a;
    border-radius: 4px;
    transition: width 220ms cubic-bezier(0.22, 1, 0.36, 1), background-color 200ms ease-out;
  }

  .card-progress-label {
    flex: 0 0 auto;
    font-size: 11px;
    font-weight: 600;
    /* color is overridden by .date-red / .date-orange / .date-black below */
    color: #5a5a5a;
    min-width: 30px;
    text-align: right;
    letter-spacing: 0.02em;
    transition: color 200ms ease-out;
  }

  /* Urgency-matched colors: progress bar and % label share the date's color */
  .card-progress-row.date-red .card-progress-bar-fill {
    background: #f53636;
  }
  .card-progress-row.date-red .card-progress-label {
    color: #f53636;
  }

  .card-progress-row.date-orange .card-progress-bar-fill {
    background: #ffa500;
  }
  .card-progress-row.date-orange .card-progress-label {
    color: #ffa500;
  }

  .card-progress-row.date-black .card-progress-bar-fill {
    background: #5a5a5a;
  }
  .card-progress-row.date-black .card-progress-label {
    color: #5a5a5a;
  }

  /* Dark theme: the neutral (non-urgent) state only. #5a5a5a is a mid-dark grey
     chosen for a #eee track on a white card; in dark mode the track resolves to
     #333 and the card to #232322, so both the bar and its label all but vanish.
     Red and orange are left alone — they already carry enough luminance.

     Scoped on .wx-willow-dark-theme (the wx-kanban vendor root, set from
     $vuetify.theme.isDark) to match the dark rules at the top of this
     stylesheet; the .theme--dark block further up belongs to the Vuetify
     copy-link menu, not to this vendor DOM.

     Specificity note: the .date-black selectors below must repeat the full
     .card-progress-row.date-black chain (0-4-0) to beat the 0-3-0 rules above.
     The bare .card-progress-bar-fill override (0-2-0) covers the default. */
  .wx-willow-dark-theme .card-progress-bar-fill,
  .wx-willow-dark-theme .card-progress-row.date-black .card-progress-bar-fill {
    background: #9e9e9e;
  }

  .wx-willow-dark-theme .card-progress-label,
  .wx-willow-dark-theme .card-progress-row.date-black .card-progress-label {
    color: #b0b0b0;
  }

  /* Due date row (body metadata, above the progress bar) */
  .card-date-row {
    display: flex;
    align-items: center;
    padding-bottom: 4px;
  }

  /* Footer: actions LEFT | info RIGHT, single split row.
     Selector includes .svelte-vhwr63 to match vendor specificity (0-3-0)
     and beat the vendor's flex-direction: column that would otherwise leak.
     This only works while .wx-footer has exactly TWO direct children — see the
     "no .wx-card-icons wrapper" note in cardTemplate. */
  .wx-footer.svelte-vhwr63.wx-with-content {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .card-footer-actions {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  /* margin-left: auto rather than relying on space-between alone.
     renderIsolateButtonHTML returns '' when a task has no isolation data and
     nothing to isolate (see its `return ''` branches), and the verify button
     only renders on a Verification Board — so .card-footer-actions really can
     come out empty, and space-between on a single child would let this cluster
     drift to the left edge. margin-left: auto makes the right-flush
     unconditional. */
  .card-footer-info {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
  }

  .wx-label.svelte-kqkezg span {
      display: flex;
      max-width: 200px;
  }
</style>
